<template>
  <div id="globusauth" v-if="showGlobusLogin">
    <b-alert v-for="error in errors" :key="error" :show="showErrors" variant="danger">{{config.errorCodes[error]}}</b-alert>
    <b-alert :show="showAuthError" variant="danger">Please attempt to login again. If this error persists contact an administrator.</b-alert>
    <div v-if="!authenticated">
      <b-button class="btn-swipes" @click="loginWithGlobus">Login with Globus</b-button>
    </div>
    <div>
      <br>
      <div class="info">
        <p>Globus unifies logins across institutions by using each organization's authentication system.</p>
        <p>Read the <a href="https://docs.globus.org/how-to/get-started/" target="_blank">globus documentation</a> to learn more.</p>
      </div>
    </div>
  </div>
</template>

<script>
// Reference: https://github.com/bpedroza/js-pkce
import WordArray from 'crypto-js/lib-typedarrays';
import PkceAuth from '../../Auth';

export default {
  name: 'globusauth',
  data() {
    return {
      /**
       * whether the user has authenticated with globus
       */
      authenticated: false,
      /**
       * errors recieved via router query
       */
      errors: [],
      showErrors: false,
      /**
       * errors from globus auth
       */
      showAuthError: false,
    };
  },
  props: {
    /**
     * token generated by globus auth
     */
    globusToken: {
      type: String,
      required: true,
    },
    /**
     * the authenticated user object from firebase
     */
    userInfo: {
      type: Object,
      required: true,
    },
    /**
     * errors produced by brainswipes
     */
    config: {
      type: Object,
      required: true,
    },
    /**
     * The page to redirect to after authentication
     */
    redirectPath: {
      type: String,
      required: true,
    },
    /**
     * The component to load after globus login
     */
    redirectComponent: {
      type: String,
      required: true,
    },
    /**
     * whether to show the globus login button
     */
    showGlobusLogin: {
      type: Boolean,
      required: true,
    },
  },
  methods: {
    /**
     * create url params and redirect user to globus
     */
    loginWithGlobus() {
      const additionalParams = { state: sessionStorage.getItem('pkce_state') };
      const authUrl = PkceAuth.authorizeUrl(additionalParams);
      window.location.replace(authUrl);
    },
    /**
     * check if the user was redirected from globus with a token
     */
    checkForGlobusAuthCode() {
      const params = new URLSearchParams(window.location.search);
      const authCode = params.get('code');
      if (authCode) {
        const url = window.location.href;
        PkceAuth.exchangeForAccessToken(url).then((resp) => {
          const accessToken = resp.access_token;
          this.$emit('globusLogin', accessToken);

          // This isn't strictly necessary but it ensures no code reuse.
          sessionStorage.removeItem('pkce_code_verifier');
          sessionStorage.removeItem('pkce_state');

          this.$router.push({ name: this.redirectComponent });
        }).catch((e) => {
          console.log(e);
          this.showAuthError = true;
        });
      }
    },
    /**
     * show content that requires authentication
     */
    allowAccess() {
      if (this.globusToken) {
        this.authenticated = true;
      }
    },
    /**
     * check for incoming errors and display them
     */
    parseErrors() {
      const errors = this.$route.query.errors;
      if (errors) {
        this.showErrors = true;
        this.errors = errors;
      }
    },
    /**
     * The redirect uri should match the host currently running the app.
     * https://auth.globus.org/v2/web/developers
     */
    setRedirect() {
      let redirectUri = `https://brainswipes.us/${this.redirectPath}`;
      if (process.env.NODE_ENV === 'development') {
        redirectUri = `http://localhost:8080/${this.redirectPath}`;
      }
      PkceAuth.config.redirect_uri = redirectUri;
    },
    /**
     * set state and add it to session storage
     */
    setPkceState() {
      if (sessionStorage.getItem('pkce_state') === null) {
        sessionStorage.setItem('pkce_state', WordArray.random(64));
        sessionStorage.setItem('pkce_code_verifier', WordArray.random(64));
      }
      PkceAuth.state = sessionStorage.getItem('pkce_state');
      PkceAuth.codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    },
  },
  created() {
    this.setRedirect();
    this.setPkceState();
    this.checkForGlobusAuthCode();
    this.allowAccess();
    this.parseErrors();
  },
};
</script>

<style scoped>
  .info{
    background-color: white;
    padding: 5px;
    margin: 5px;
  }
</style>


// WEBPACK FOOTER //
// src/components/Widgets/GlobusAuth.vue